import {
  type DrawingCanvasShape,
  type DrawingCanvasShapes,
  DrawingCanvasShapeType,
  type Point,
} from '@civilgrid/ui/components';
import chroma from 'chroma-js';
import { generateKeyBetween } from 'fractional-indexing';
import { isEqual } from 'lodash';
import type {
  MapDetail,
  LineObject as MapLineObject,
  MapObject,
  PolygonObject as MapPolygonObject,
} from '~/common/types';
import { BRIGHT_COLORS, PRESET_COLORS } from '~/constants/color';

type ToCanvasCoords = (position: GeoJSON.Position) => Point;
type ToLngLat = (point: Point) => GeoJSON.Position;

export type NewMapObject =
  | Omit<MapLineObject, 'createdBy' | 'updatedBy'>
  | Omit<MapPolygonObject, 'createdBy' | 'updatedBy'>;

const NEW_OBJECT_COLORS = PRESET_COLORS.filter((color) => ![...BRIGHT_COLORS, 'transparent'].includes(color.hex));

export const isLineOrPolygon = (mapObject: MapObject): mapObject is MapLineObject | MapPolygonObject =>
  mapObject.type === 'line' || mapObject.type === 'polygon';

// Plain string comparison; localeCompare can put fractional-index keys in the wrong order
const compareSortOrders = (a: string, b: string) => (a === b ? 0 : a < b ? -1 : 1);

const isClosedRing = (ring: GeoJSON.Position[]) => ring.length > 1 && isEqual(ring[0], ring[ring.length - 1]);

const toRgba = (color: string, opacity: number) => chroma(color).alpha(opacity).css();

const toCanvasShape = (
  mapObject: MapLineObject | MapPolygonObject,
  order: number,
  toCanvasCoords: ToCanvasCoords,
): DrawingCanvasShape => {
  const { color, strokeOpacity, strokeWidth } = mapObject.properties;
  if (mapObject.type === 'line') {
    return {
      key: mapObject.id,
      type: DrawingCanvasShapeType.LINE,
      points: mapObject.geometry.coordinates.map(toCanvasCoords),
      order,
      style: {
        strokeColor: toRgba(color, strokeOpacity),
        lineWidth: strokeWidth,
        dashed: mapObject.properties.strokeStyle === 'dashed',
      },
    };
  }
  const [outerRing] = mapObject.geometry.coordinates;
  return {
    key: mapObject.id,
    type: DrawingCanvasShapeType.POLYGON,
    // DrawingCanvas draws only the outer ring, and closes it itself
    points: (isClosedRing(outerRing) ? outerRing.slice(0, -1) : outerRing).map(toCanvasCoords),
    order,
    style: {
      strokeColor: toRgba(color, strokeOpacity),
      fillColor: toRgba(color, mapObject.properties.fillOpacity),
      lineWidth: strokeWidth,
    },
  };
};

/** Converts a map's lines and polygons to canvas shapes in canvas pixels, leaving out other object types. */
export const toCanvasShapes = (mapObjects: MapDetail['objects'], toCanvasCoords: ToCanvasCoords): DrawingCanvasShapes =>
  Object.fromEntries(
    Object.values(mapObjects)
      .filter(isLineOrPolygon)
      .sort((a, b) => compareSortOrders(a.sortOrder, b.sortOrder))
      .map((mapObject, order) => [mapObject.id, toCanvasShape(mapObject, order, toCanvasCoords)]),
  );

/** Moves a line or polygon to an edited canvas shape's points, keeping a polygon's holes. */
export const toUpdatedMapObject = (
  mapObject: MapLineObject | MapPolygonObject,
  canvasShape: DrawingCanvasShape,
  toLngLat: ToLngLat,
): MapLineObject | MapPolygonObject => {
  const coordinates = canvasShape.points.map(toLngLat);
  if (mapObject.type === 'line') return { ...mapObject, geometry: { type: 'LineString', coordinates } };
  const [outerRing, ...holes] = mapObject.geometry.coordinates;
  const newOuterRing = isClosedRing(outerRing) ? [...coordinates, coordinates[0]] : coordinates;
  return { ...mapObject, geometry: { type: 'Polygon', coordinates: [newOuterRing, ...holes] } };
};

// Counts every object type, so a new object sorts above the ones this view leaves out too
const nextSortOrder = (mapObjects: MapDetail['objects']) => {
  const [lastSortOrder] = Object.values(mapObjects)
    .map((mapObject) => mapObject.sortOrder)
    .sort((a, b) => compareSortOrders(b, a));
  return generateKeyBetween(lastSortOrder ?? null, null);
};

/** Builds a map object from a drawn canvas line or polygon, with the old drawing tools' defaults. */
export const toNewMapObject = (
  canvasShape: DrawingCanvasShape,
  mapObjects: MapDetail['objects'],
  toLngLat: ToLngLat,
): NewMapObject | undefined => {
  const coordinates = canvasShape.points.map(toLngLat);
  const base = { id: canvasShape.key, sortOrder: nextSortOrder(mapObjects), description: '' };
  const style = {
    color: NEW_OBJECT_COLORS[Math.floor(Math.random() * NEW_OBJECT_COLORS.length)].hex,
    strokeOpacity: 1,
    strokeWidth: 4,
    strokeStyle: 'solid' as const,
  };
  switch (canvasShape.type) {
    case DrawingCanvasShapeType.LINE:
      return {
        ...base,
        type: 'line',
        title: 'New Line',
        geometry: { type: 'LineString', coordinates },
        properties: { ...style, showLength: true, showTitle: true, labelAlwaysVisible: false },
      };
    case DrawingCanvasShapeType.POLYGON:
      return {
        ...base,
        type: 'polygon',
        title: 'New Polygon',
        geometry: { type: 'Polygon', coordinates: [[...coordinates, coordinates[0]]] },
        properties: { ...style, fillOpacity: 0.25, showArea: true, showTitle: true, labelAlwaysVisible: false },
      };
    default:
      return undefined;
  }
};
